/**
 * Event-type predicates shared by the registration UI.
 *
 * Header/Heeler is a team-roping concept. Solo disciplines (barrel racing,
 * tie-down, breakaway, bareback, saddle bronc, bull riding, etc.) have a
 * single contestant per registration and the role picker should be hidden.
 *
 * Discipline is signalled by `event_name` and the `trig_event_type_name`
 * denormalised column. We compare on a normalised form (lower-cased,
 * collapsed whitespace) so seed strings like "Team Roping" / "team_roping"
 * / "team  roping" all match. Novice variants (`Novice Team Roping`) are
 * stripped to their base discipline before comparison so they share every
 * code path with the non-novice version.
 */

/**
 * Loose shape accepted by the helpers below. Any object that *might* expose
 * one of these fields is fine; unknown extra fields are allowed via the index
 * signature so callers passing API records (with `[key: string]: unknown`) and
 * concrete domain types (e.g. `JackpotEventDraft`) both match.
 */
type MaybeEvent =
  | {
      event_name?: string | null;
      event_type?: string | null;
      trig_event_type_name?: string | null;
    }
  | {
      event_name?: string | null;
      event_type?: string | null;
      trig_event_type_name?: string | null;
      [key: string]: unknown;
    };

const TEAM_ROPING_KEY = 'team roping';
const NOVICE_PREFIX_NORMALIZED = 'novice ';

function normalize(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Strip the leading `novice ` segment so novice variants match their base. */
function stripNovice(value: string): string {
  return value.startsWith(NOVICE_PREFIX_NORMALIZED)
    ? value.slice(NOVICE_PREFIX_NORMALIZED.length)
    : value;
}

export function isTeamRopingEvent(event: MaybeEvent | null | undefined): boolean {
  if (!event) return false;
  const candidates = [event.event_name, event.trig_event_type_name];
  return candidates.some((c) => stripNovice(normalize(c)) === TEAM_ROPING_KEY);
}

/**
 * Resolve the human-facing title for an event.
 *
 * Custom `event_name` is the override; if blank, fall back to the event type
 * (preferring the denormalised trigger column, then the wizard-side
 * `event_type` string). Centralised here so list, detail, calendar, receipt,
 * and notification surfaces never drift apart.
 */
export function getEventDisplayTitle(
  event: MaybeEvent | null | undefined,
  fallback: string = 'Event',
): string {
  if (!event) return fallback;
  const customName = typeof event.event_name === 'string' ? event.event_name.trim() : '';
  if (customName) return customName;
  const trigType =
    typeof event.trig_event_type_name === 'string' ? event.trig_event_type_name.trim() : '';
  if (trigType) return trigType;
  const eventType = typeof event.event_type === 'string' ? event.event_type.trim() : '';
  if (eventType) return eventType;
  return fallback;
}

/**
 * Sort events alphabetically by their display title, for surfaces that show an
 * unordered *set* of a rodeo's events (e.g. the event badges on a rodeo card).
 * Do NOT use it on day-sheet / draw / live-scoring lists — those are ordered by
 * run order on purpose.
 *
 * `localeCompare` with `numeric` so names like "#10.5 Roping" or "2D Barrels"
 * order the way a human reads them rather than by codepoint. Returns a new
 * array; the input may be a React Query cache entry we must not mutate.
 */
export function sortEventsByDisplayTitle<T extends MaybeEvent>(events: readonly T[]): T[] {
  return [...events].sort((a, b) =>
    getEventDisplayTitle(a).localeCompare(getEventDisplayTitle(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );
}
